import React, { useState, useEffect } from 'react';
import axiosInstance from '../../apis/axiosConfig';
import { toast } from 'react-toastify';
import { FaUsers, FaSearch, FaUserShield, FaUserCheck, FaUserTimes, FaTrash, FaUserSlash } from 'react-icons/fa';
import '../../pages_premium.css';
import './UsersList.css';
import AdminPagination from '../../components/common/AdminPagination';

function UsersList() {
    const [users, setUsers] = useState([]);
    const [search, setSearch] = useState('');
    const [currentPage, setCurrentPage] = useState(1);
    const [pageSize, setPageSize] = useState(10);

    useEffect(() => {
        fetchUsers();
    }, []);

    const fetchUsers = async () => {
        try {
            const response = await axiosInstance.get('/admin/users');
            setUsers(response.data);
        } catch (error) {
            console.error('Error fetching users:', error);
            if (error?.response?.status === 401 || error?.response?.status === 403) {
                toast.error('Session expired. Please logout and login again.');
                localStorage.removeItem('token');
            } else {
                toast.error('Failed to load users');
            }
        }
    };

    const toggleUserStatus = async (userId, currentStatus) => {
        try {
            const newStatus = currentStatus === 'active' ? 'banned' : 'active';
            await axiosInstance.put(`/admin/users/${userId}/status`, { status: newStatus });
            toast.success(`User marked as ${newStatus}`);
            fetchUsers();
        } catch (error) {
            console.error('Error updating status:', error);
            toast.error('Failed to update status');
        }
    };

    const deleteUser = async (userId, userName) => {
        if (!window.confirm(`Are you sure you want to delete "${userName}"? This cannot be undone.`)) return;
        try {
            await axiosInstance.delete(`/admin/users/${userId}`);
            toast.success('User deleted successfully');
            fetchUsers();
        } catch (error) {
            console.error('Error deleting user:', error);
            toast.error(error?.response?.data?.message || 'Failed to delete user');
        }
    };

    const getDisplayRole = (role) => {
        if (role === 'owner') return 'Business Owner';
        if (role === 'agent') return 'Lister';
        return role;
    };

    const nonAdminUsers = users.filter(u => u.role !== 'admin' && u.email?.toLowerCase() !== 'admin@gmail.com' && u.email?.toLowerCase() !== 'joyelpaul22@gmail.com');

    const filtered = nonAdminUsers.filter(u =>
        u.name?.toLowerCase().includes(search.toLowerCase()) ||
        u.email?.toLowerCase().includes(search.toLowerCase()) ||
        getDisplayRole(u.role)?.toLowerCase().includes(search.toLowerCase())
    );

    useEffect(() => {
        setCurrentPage(1);
    }, [search]);

    const paginatedUsers = filtered.slice((currentPage - 1) * pageSize, currentPage * pageSize);

    const getInitials = (name) => {
        if (!name) return '?';
        return name.split(' ').map(n => n[0]).join('').toUpperCase().slice(0, 2);
    };

    const getRoleClass = (role) => {
        if (role === 'admin') return 'ptag ptag-admin';
        if (role === 'agent') return 'ptag ptag-agent';
        if (role === 'owner') return 'ptag ptag-owner';
        return 'ptag ptag-student';
    };

    return (
        <div className="page-premium-wrapper users-list-page">
            <div className="page-premium-content">

                {/* Hero Header */}
                <div className="page-hero-header page-hero-users">
                    <div className="ph-blob ph-blob-1"></div>
                    <div className="ph-blob ph-blob-2"></div>

                    <div className="ph-left">
                        <div className="ph-icon">
                            <FaUsers size={28} />
                        </div>
                        <div>
                            <h1 className="ph-title">Users Management</h1>
                            <p className="ph-subtitle">Manage all registered users, roles and access control</p>
                        </div>
                    </div>

                    <div className="ph-right">
                        <div className="ph-count-badge">
                            <span className="count-num">{nonAdminUsers.length}</span>
                            <span className="count-lbl">Total Users</span>
                        </div>
                    </div>
                </div>

                {/* Table Card */}
                <div className="premium-table-card">
                    <div className="ptable-toolbar">
                        <div className="ptable-title">
                            <span className="ptable-title-dot ptable-title-dot-pink"></span>
                            All Users
                        </div>
                        <div className="ptable-search">
                            <FaSearch className="ptable-search-icon" />
                            <input
                                type="text"
                                placeholder="Search users..."
                                value={search}
                                onChange={(e) => setSearch(e.target.value)}
                            />
                        </div>
                    </div>

                    <div className="ptable-wrap">
                        <table className="premium-table">
                            <thead>
                                <tr>
                                    <th>#</th>
                                    <th>User</th>
                                    <th>Email</th>
                                    <th>Role</th>
                                    <th>Status</th>
                                    <th>Actions</th>
                                </tr>
                            </thead>
                            <tbody>
                                {paginatedUsers.map((user, idx) => (
                                    <tr key={user.id}>
                                        <td className="user-index">{(currentPage - 1) * pageSize + idx + 1}</td>
                                        <td>
                                            <div className="user-name-cell">
                                                <div className="user-avatar">{getInitials(user.name)}</div>
                                                <strong>{user.name}</strong>
                                            </div>
                                        </td>
                                        <td className="user-email">{user.email}</td>
                                        <td>
                                            <span className={getRoleClass(user.role)}>
                                                {user.role === 'admin' && <FaUserShield />}
                                                {getDisplayRole(user.role)}
                                            </span>
                                        </td>
                                        <td>
                                            <span className={`ptag ${user.status === 'active' ? 'ptag-active' : 'ptag-banned'}`}>
                                                {user.status === 'active' ? <FaUserCheck /> : <FaUserTimes />}
                                                {user.status}
                                            </span>
                                        </td>
                                        <td>
                                            <div className="user-actions">
                                                {user.role !== 'admin' && (
                                                    <button
                                                        className={`action-btn ${user.status === 'active' ? 'action-btn-ban' : 'action-btn-activate'}`}
                                                        data-tooltip={user.status === 'active' ? 'Ban User' : 'Activate User'}
                                                        onClick={() => toggleUserStatus(user.id, user.status)}
                                                    >
                                                        {user.status === 'active' ? <FaUserSlash /> : <FaUserCheck />}
                                                    </button>
                                                )}
                                                <button
                                                    className="action-btn action-btn-delete"
                                                    data-tooltip="Delete User"
                                                    onClick={() => deleteUser(user.id, user.name)}
                                                >
                                                    <FaTrash />
                                                </button>
                                            </div>
                                        </td>
                                    </tr>
                                ))}
                                {filtered.length === 0 && (
                                    <tr>
                                        <td colSpan="6">
                                            <div className="ptable-empty">
                                                <div className="ptable-empty-icon">👤</div>
                                                <p>No users found.</p>
                                            </div>
                                        </td>
                                    </tr>
                                )}
                            </tbody>
                        </table>
                    </div>

                    <AdminPagination
                        totalItems={filtered.length}
                        currentPage={currentPage}
                        pageSize={pageSize}
                        onPageChange={setCurrentPage}
                        onPageSizeChange={(newSize) => { setPageSize(newSize); setCurrentPage(1); }}
                        itemLabel="users"
                    />
                </div>

            </div>
        </div>
    );
}

export default UsersList;
